import React from "react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/brand/BrandLogo";
import SEO from "@/brand/SEO";
import { Compass, ShieldCheck, Sparkles, Heart, BookOpenCheck, FileDown } from "lucide-react";

function Pill({ children }) {
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-md-border bg-md-surface px-3 py-1.5 text-xs font-medium text-md-text-2">
            {children}
        </span>
    );
}

function FeatureCard({ icon, title, desc }) {
    return (
        <div className="rounded-2xl border border-md-border bg-md-surface p-6">
            <div className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-md-surface-2 text-md-text">
                {icon}
            </div>
            <h3 className="text-lg font-semibold tracking-tight text-md-text">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-md-text-2">{desc}</p>
        </div>
    );
}

export default function PublicHome() {
    return (
        <div data-testid="public-home">
            <SEO
                title="Aventure collaborative pour collectionneurs Pokémon"
                description="Mission Dresseur n'est pas une marketplace. C'est une mission humaine de recherche confiée à des dresseurs sérieux pour t'aider à compléter ton classeur Pokémon."
                canonical="https://missiondresseur.fr/"
                og={{ template: "default", title: "Les doubles des uns complètent les classeurs des autres.", subtitle: "Aventure collaborative pour collectionneurs Pokémon", eyebrow: "DOCTRINE" }}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    name: "Mission Dresseur",
                    url: "https://missiondresseur.fr/",
                    inLanguage: "fr-FR",
                    description: "Aventure collaborative pour collectionneurs Pokémon — l'inverse d'une marketplace.",
                }}
            />
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-md-border bg-md-surface">
                <div className="md-grain absolute inset-0 opacity-50" />
                <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:py-32">
                    <div className="max-w-3xl">
                        <Pill><Sparkles className="h-3.5 w-3.5 text-md-secondary" /> Phase 1 — Bêta privée</Pill>
                        <h1 className="md-h1 mt-6 text-md-text">
                            Les doubles des uns complètent
                            <span className="block text-md-secondary">les classeurs des autres.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-md-text-2 sm:text-lg">
                            Mission Dresseur n’est pas une marketplace. C’est une mission humaine de recherche
                            confiée à des dresseurs sérieux pour t’aider à compléter ton classeur Pokémon.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/register"
                                data-testid="hero-cta-register"
                                className="inline-flex h-12 items-center rounded-full bg-md-primary px-6 text-sm font-semibold text-md-primary-fg hover:opacity-90"
                            >
                                <Compass className="mr-2 h-4 w-4" /> Lancer ma première mission
                            </Link>
                            <Link
                                to="/devenir-dresseur"
                                data-testid="hero-cta-seller"
                                className="inline-flex h-12 items-center rounded-full border border-md-border bg-transparent px-6 text-sm font-semibold text-md-text hover:bg-md-surface-2"
                            >
                                Devenir dresseur certifié
                            </Link>
                            <a
                                href={`${process.env.REACT_APP_BACKEND_URL}/api/public/doctrine.pdf`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-cta-doctrine"
                                className="inline-flex h-12 items-center rounded-full bg-md-secondary px-6 text-sm font-semibold text-md-secondary-fg hover:opacity-90"
                            >
                                <FileDown className="mr-2 h-4 w-4" /> Lire la doctrine
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pillars */}
            <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
                <div className="mb-10">
                    <p className="md-overline">Notre doctrine</p>
                    <h2 className="md-h2 mt-2 text-md-text">Un produit qui respecte ton temps.</h2>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    <FeatureCard
                        icon={<Compass className="h-5 w-5" />}
                        title="Une mission, un dresseur"
                        desc="Quand un dresseur prend ta mission, il a l’exclusivité. Tu protèges son temps, il protège ton classeur."
                    />
                    <FeatureCard
                        icon={<ShieldCheck className="h-5 w-5" />}
                        title="Prix fixes par la plateforme"
                        desc="Pas de négociation centime par centime. Tu raisonnes en mission globale, pas carte par carte."
                    />
                    <FeatureCard
                        icon={<Heart className="h-5 w-5" />}
                        title="Vraies cartes, vraies personnes"
                        desc="Pas de spéculation, pas de chat libre, pas de drama. Juste des classeurs qui avancent."
                    />
                    <FeatureCard
                        icon={<BookOpenCheck className="h-5 w-5" />}
                        title="Cartes pour binder"
                        desc="On vise les cartes Near Mint, propres, prêtes pour ton classeur. Pas les pièces premium instables."
                    />
                    <FeatureCard
                        icon={<Sparkles className="h-5 w-5" />}
                        title="Photos qui rassurent"
                        desc="Quelques photos par proposition. Pour te montrer le soin, pas pour transformer la plateforme en tribunal."
                    />
                    <FeatureCard
                        icon={<BrandMarkInline />}
                        title="Multi-TCG par design"
                        desc="On commence par Pokémon FR. L’architecture est prête pour d’autres jeux et langues."
                    />
                </div>
            </section>

            {/* CTA strip */}
            <section className="border-t border-md-border bg-md-surface">
                <div className="mx-auto flex max-w-6xl flex-col items-start gap-6 px-4 py-16 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                    <div className="max-w-xl">
                        <p className="md-overline">Prêt à fouiller ?</p>
                        <h3 className="mt-2 text-2xl font-bold tracking-tight text-md-text">
                            Ton classeur t’attend.
                        </h3>
                        <p className="mt-2 text-sm text-md-text-2">
                            Lance une mission ou propose tes doubles. La plateforme s’occupe du reste.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Link
                            to="/register"
                            className="inline-flex h-12 items-center rounded-full bg-md-primary px-6 text-sm font-semibold text-md-primary-fg hover:opacity-90"
                            data-testid="strip-cta-register"
                        >
                            Créer mon compte
                        </Link>
                        <Link
                            to="/comment-ca-marche"
                            className="inline-flex h-12 items-center rounded-full border border-md-border bg-transparent px-6 text-sm font-semibold text-md-text hover:bg-md-surface-2"
                        >
                            Comment ça marche
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

function BrandMarkInline() {
    return (
        <span className="inline-flex items-center justify-center">
            <BrandLogo variant="compact" size="sm" to={null} />
        </span>
    );
}
